"use client"

import { useState, useEffect, useRef } from "react"
import { useSelector } from "react-redux"
import axios from "axios"
import { Box, styled, Typography } from "@mui/material"

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000"

// Styled component for the running message animation
const RunningMessageContainer = styled(Box)(({ theme }) => ({
  whiteSpace: "nowrap",
  overflow: "hidden",
  position: "sticky",
  top: 0,
  zIndex: 1000,
  padding: theme.spacing(1),
  backgroundColor: "rgba(255, 255, 255, 0.95)", // Slightly transparent background
  color: theme.palette.primary.dark,
  fontWeight: 500,
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
  transition: "all 0.3s ease",
  cursor: "pointer",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",

  "&:hover": {
    boxShadow: "0 0 15px rgba(37, 211, 102, 0.3)", // Subtle glow effect
    transform: "translateY(-2px)", // Slight lift effect
    "& .message-content": {
      animationPlayState: "paused", // Pause the animation on hover
      color: theme.palette.primary.main, // Change text color
      textShadow: "0 0 5px rgba(37, 211, 102, 0.3)", // Text glow
    },
    "&::before, &::after": {
      background: `linear-gradient(to right, rgba(37, 211, 102, 0.1), transparent)`, // Change gradient on hover
    },
  },

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "30px",
    height: "100%",
    background: "linear-gradient(to right, rgba(255,255,255,0.9), transparent)",
    zIndex: 1,
    transition: "background 0.3s ease",
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: "30px",
    height: "100%",
    background: "linear-gradient(to left, rgba(255,255,255,0.9), transparent)",
    zIndex: 1,
    transition: "background 0.3s ease",
  },
}))

const RunningMessageContent = styled("div")({
  display: "inline-block",
  paddingLeft: "100%", // Start from the right edge
  animation: "marquee 15s linear infinite",
  "@keyframes marquee": {
    "0%": { transform: "translateX(0)" },
    "100%": { transform: "translateX(-100%)" },
  },
  transition: "color 0.3s ease, text-shadow 0.3s ease",
})

// Styled component for the credits display
const CreditsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
}))

const CreditPill = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(240, 240, 240, 0.9)",
  borderRadius: 50,
  padding: theme.spacing(1, 2),
  fontWeight: 500,
  fontSize: "0.9rem",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 150,
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
}))

const RunningMessage = ({ sx }) => {
  const [message, setMessage] = useState("")
  const contentRef = useRef(null)
  const [isPaused, setIsPaused] = useState(false)
  const { userInfo } = useSelector((state) => state.auth)

  const [credits, setCredits] = useState({
    buttonCredits: 0,
    internationalCredits: 0,
    domesticCredits: 0,
  })

  const fetchRunningMessage = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/campaign-config/running-message`)
      if (data && data.message) {
        setMessage(data.message)
      }
    } catch (error) {
      console.error("Failed to fetch running message:", error)
    }
  }

  const fetchUserCredits = async () => {
    if (!userInfo || userInfo.user.role === "admin") return

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.get(`${API_URL}/api/users/profile/credits`, config)
      if (data) {
        setCredits({
          buttonCredits: data.buttonCredits || 0,
          internationalCredits: data.internationalCredits || 0,
          domesticCredits: data.domesticCredits || 0,
        })
      }
    } catch (error) {
      console.error("Failed to fetch user credits:", error)
    }
  }

  useEffect(() => {
    fetchRunningMessage()
    fetchUserCredits()
    const messageIntervalId = setInterval(fetchRunningMessage, 60000) // Fetch message every 60 seconds
    const creditsIntervalId = setInterval(fetchUserCredits, 30000) // Fetch credits every 30 seconds

    return () => {
      clearInterval(messageIntervalId)
      clearInterval(creditsIntervalId)
    } // Cleanup intervals on unmount
  }, [userInfo])

  // Adjust animation duration based on message length
  useEffect(() => {
    if (contentRef.current && message) {
      const contentWidth = contentRef.current.offsetWidth
      const duration = Math.max(6, contentWidth / 100)

      contentRef.current.style.animationDuration = `${duration}s`
    }
  }, [message])

  const handleMouseEnter = () => {
    setIsPaused(true)
  }

  const handleMouseLeave = () => {
    setIsPaused(false)
  }

  // Check if user is admin or not
  const isAdmin = userInfo?.user?.role === "admin"

  if (!message) return null

  return (
    <Box>
      <RunningMessageContainer sx={sx} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <RunningMessageContent
          ref={contentRef}
          className="message-content"
          style={{
            animationPlayState: isPaused ? "paused" : "running",
          }}
        >
          {message}
        </RunningMessageContent>
      </RunningMessageContainer>

      {/* Show credits for users and resellers only */}
      {!isAdmin && userInfo?.user && (
        <CreditsContainer>
          <CreditPill>
            <Typography variant="body2">Button Credits: {credits.buttonCredits}</Typography>
          </CreditPill>
          <CreditPill>
            <Typography variant="body2">Int. Credits: {credits.internationalCredits}</Typography>
          </CreditPill>
          <CreditPill>
            <Typography variant="body2">Dom. Credits: {credits.domesticCredits}</Typography>
          </CreditPill>
        </CreditsContainer>
      )}
    </Box>
  )
}

export default RunningMessage
